import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ItfDatabase } from '../models/itf-database.model';
import { ItfPago } from '../models/itf-pago.model';

@Injectable({
  providedIn: 'root'
})
export class ItfDatabaseService {
  private apiUrl = `${environment.apiUrl}/itf`;

  constructor(private http: HttpClient) {}

  // ============================================================================
  // ITF DATABASE OPERATIONS
  // ============================================================================

  getAll(): Observable<ItfDatabase[]> {
    return this.http.get<any[]>(this.apiUrl).pipe(
      map(data => data.map(item => ItfDatabase.fromApiData(item)))
    );
  }

  getById(id: number): Observable<ItfDatabase> {
    return this.http.get<any>(`${this.apiUrl}/${id}`).pipe(
      map(data => ItfDatabase.fromApiData(data))
    );
  }

  getByNroLibreta(nroLibreta: number): Observable<ItfDatabase[]> {
    return this.http.get<any[]>(`${this.apiUrl}/nro-libreta/${nroLibreta}`).pipe(
      map(data => data.map(item => ItfDatabase.fromApiData(item)))
    );
  }

  filter(filters: any): Observable<ItfDatabase[]> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params = params.append(key, filters[key]);
      }
    });

    return this.http.get<any[]>(`${this.apiUrl}/filter`, { params }).pipe(
      map(data => data.map(item => ItfDatabase.fromApiData(item)))
    );
  }

  create(itfData: ItfDatabase): Observable<ItfDatabase> {
    const payload = {
      nroLibreta: itfData.nroLibreta,
      nombre: itfData.nombre,
      apellido: itfData.apellido,
      fcNacimiento: itfData.fcNacimiento,
      sexo: itfData.sexo,
      gup: itfData.gup,
      nuCertDan: itfData.nuCertDan,
      dan: itfData.dan,
      ngb: itfData.ngb,
      nacionalidad: itfData.nacionalidad,
      emailNgb: itfData.emailNgb,
      activo: itfData.activo,
      createdBy: itfData.createdBy
    };

    return this.http.post<any>(`${this.apiUrl}`, payload).pipe(
      map(data => ItfDatabase.fromApiData(data))
    );
  }

  update(id: number, itfData: Partial<ItfDatabase>): Observable<ItfDatabase> {
    const payload: any = {};

    // Only include fields that are defined
    if (itfData.nroLibreta !== undefined) payload.nroLibreta = itfData.nroLibreta;
    if (itfData.nombre !== undefined) payload.nombre = itfData.nombre;
    if (itfData.apellido !== undefined) payload.apellido = itfData.apellido;
    if (itfData.fcNacimiento !== undefined) payload.fcNacimiento = itfData.fcNacimiento;
    if (itfData.sexo !== undefined) payload.sexo = itfData.sexo;
    if (itfData.gup !== undefined) payload.gup = itfData.gup;
    if (itfData.nuCertDan !== undefined) payload.nuCertDan = itfData.nuCertDan;
    if (itfData.dan !== undefined) payload.dan = itfData.dan;
    if (itfData.ngb !== undefined) payload.ngb = itfData.ngb;
    if (itfData.nacionalidad !== undefined) payload.nacionalidad = itfData.nacionalidad;
    if (itfData.emailNgb !== undefined) payload.emailNgb = itfData.emailNgb;

    return this.http.put<any>(`${this.apiUrl}/${id}`, payload).pipe(
      map(data => ItfDatabase.fromApiData(data))
    );
  }

  softDelete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  restore(id: number): Observable<ItfDatabase> {
    return this.http.post<any>(`${this.apiUrl}/${id}/restore`, {}).pipe(
      map(data => ItfDatabase.fromApiData(data))
    );
  }

  // ============================================================================
  // STATISTICS
  // ============================================================================

  getEstadisticas(anio?: number): Observable<any> {
    let params = new HttpParams();
    if (anio) {
      params = params.append('anio', anio.toString());
    }
    return this.http.get<any>(`${this.apiUrl}/estadisticas`, { params });
  }

  getDanesByLevel(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/danes-nivel`);
  }

  // ============================================================================
  // PAYMENT OPERATIONS
  // ============================================================================

  registrarPago(id: number, pagoData: Partial<ItfPago>): Observable<ItfPago> {
    return this.http.post<any>(`${this.apiUrl}/${id}/pagos`, pagoData).pipe(
      map(data => ItfPago.fromApiData(data))
    );
  }

  getHistorialPagos(id: number): Observable<ItfPago[]> {
    return this.http.get<any[]>(`${this.apiUrl}/${id}/pagos`).pipe(
      map(data => data.map(item => ItfPago.fromApiData(item)))
    );
  }

  updatePago(pagoId: number, pagoData: Partial<ItfPago>): Observable<ItfPago> {
    return this.http.put<any>(`${this.apiUrl}/pagos/${pagoId}`, pagoData).pipe(
      map(data => ItfPago.fromApiData(data))
    );
  }

  deletePago(pagoId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pagos/${pagoId}`);
  }

  getRecaudadoPorAnio(anio?: number): Observable<{ anio: number; total: number }> {
    let params = new HttpParams();
    if (anio) {
      params = params.append('anio', anio.toString());
    }
    return this.http.get<{ anio: number; total: number }>(`${this.apiUrl}/pagos/recaudado`, { params });
  }
}